// src/pages/ClientListPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiCheckSquare, FiBriefcase } from 'react-icons/fi';
import { db } from '../firebase'; // Adjust path
import { useAuth } from '../context/AuthContext'; // To check Manager role for delete and view tasks
import styles from '../styles/ClientListPage.module.css'; // Adjust path

const ClientListPage = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [search, setSearch] = useState('');
    const { userRole } = useAuth(); // Get user role

    useEffect(() => {
        const fetchClients = async () => {
            setLoading(true);
            setError('');
            try {
                const clientsCol = collection(db, 'clients');
                const q = query(clientsCol, orderBy('name', 'asc')); // Order clients by name
                const querySnapshot = await getDocs(q);
                const clientList = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setClients(clientList);
            } catch (err) {
                console.error("Error fetching clients:", err);
                setError("Failed to load clients.");
            } finally {
                setLoading(false);
            }
        };
        fetchClients();
    }, []);

    const handleDeleteClient = async (clientId, clientName) => {
        // Only Managers can delete
        if (userRole !== 'Manager') {
            setError("You do not have permission to delete clients.");
            return;
        }

        if (window.confirm(`Are you sure you want to delete client "${clientName || 'N/A'}"?\nThis action cannot be undone.`)) {
            setDeletingId(clientId);
            setError('');
            try {
                const clientDocRef = doc(db, 'clients', clientId);
                await deleteDoc(clientDocRef);
                setClients(prev => prev.filter(client => client.id !== clientId));
                console.log(`Client document ${clientId} deleted.`);
            } catch (err) {
                 console.error("Error deleting client document:", err);
                 setError(`Failed to delete ${clientName || 'client'}. Please try again.`);
            } finally {
                 setDeletingId(null);
            }
        }
    };

    // Check if user is a Manager for enabling delete and view tasks buttons
    const isManager = userRole === 'Manager';

    const stats = useMemo(() => {
        const s = { total: clients.length, withPhone: 0, withAddress: 0 };
        clients.forEach((c) => {
            if (c.phoneNumber) s.withPhone += 1;
            if (c.address) s.withAddress += 1;
        });
        return s;
    }, [clients]);

    const filteredClients = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return clients;
        return clients.filter((c) => {
            const hay = `${c.name || ''} ${c.phoneNumber || ''} ${c.address || ''}`.toLowerCase();
            return hay.includes(term);
        });
    }, [clients, search]);

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.title}>Clients</h2>
                    <p className={styles.subtitle}>Manage your client accounts and their linked work.</p>
                </div>
                <Link to="/clients/new" className={styles.addButton}>
                    <FiPlus /> Add Client
                </Link>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}

            {/* Stats */}
            <div className={styles.statsRow}>
                <div className={`${styles.statCard} ${styles.statTotal}`}>
                    <div className={styles.statLabel}>Total Clients</div>
                    <div className={styles.statValue}>{stats.total}</div>
                </div>
                <div className={`${styles.statCard} ${styles.statPhone}`}>
                    <div className={styles.statLabel}>With Phone</div>
                    <div className={styles.statValue}>{stats.withPhone}</div>
                </div>
                <div className={`${styles.statCard} ${styles.statAddress}`}>
                    <div className={styles.statLabel}>With Address</div>
                    <div className={styles.statValue}>{stats.withAddress}</div>
                </div>
            </div>

            {/* Toolbar */}
            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <FiSearch className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Search by name, phone or address…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingIndicator}>Loading clients...</div>
            ) : clients.length === 0 ? (
                <div className={styles.emptyState}>
                    <FiBriefcase className={styles.emptyIcon} />
                    <p>No clients yet.</p>
                    <span>Click “Add Client” to create the first one.</span>
                </div>
            ) : filteredClients.length === 0 ? (
                <div className={styles.emptyState}>
                    <FiSearch className={styles.emptyIcon} />
                    <p>No clients match your search.</p>
                </div>
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.clientTable}>
                        <thead className={styles.tableHeader}>
                            <tr>
                                <th>Client</th>
                                <th>Phone Number</th>
                                <th className={styles.addressColumn}>Address</th>
                                <th className={styles.actionsHeader}>Actions</th>
                            </tr>
                        </thead>
                        <tbody className={styles.tableBody}>
                            {filteredClients.map(client => (
                                <tr key={client.id} className={deletingId === client.id ? styles.deletingRow : ''}>
                                    <td data-label="Client">
                                        <div className={styles.personCell}>
                                            <div className={styles.avatar}>
                                                {(client.name || '?').charAt(0).toUpperCase()}
                                            </div>
                                            <div className={styles.personName}>{client.name || 'N/A'}</div>
                                        </div>
                                    </td>
                                    <td data-label="Phone">{client.phoneNumber || <span className={styles.muted}>—</span>}</td>
                                    <td data-label="Address" className={styles.addressColumn}>{client.address || <span className={styles.muted}>—</span>}</td>
                                    <td data-label="Actions" className={styles.actionsCell}>
                                        <Link
                                            to={`/clients/${client.id}/edit`}
                                            className={`${styles.actionButton} ${styles.editButton}`}
                                            title={`Edit ${client.name || 'Client'}`}
                                        >
                                            <FiEdit2 />
                                        </Link>
                                        {isManager && (
                                            <Link
                                                to={`/clients/${client.id}/tasks`}
                                                className={`${styles.actionButton} ${styles.viewTasksButton}`}
                                                title={`View tasks for ${client.name || 'Client'}`}
                                            >
                                                <FiCheckSquare />
                                            </Link>
                                        )}
                                        {isManager && (
                                            <button
                                                onClick={() => handleDeleteClient(client.id, client.name)}
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                disabled={deletingId === client.id}
                                                title={`Delete ${client.name || 'Client'}`}
                                            >
                                                <FiTrash2 />
                                            </button>
                                        )}
                                        {!isManager && (
                                            <span className={styles.noActions}>—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ClientListPage;
